import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Products from './products';
import Header from './header';
import Footer from './Footer';
import ProductModal from './ProductModal';
import styles from './CategoryProducts.module.css';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SearchProducts = ({ cartList = [], onAddToCart, cartItems = 0, cartTotal = 0, showCartDropdown = false, onCartToggle = () => {}, onRemoveItem = () => {} }) => {
    const query = useQuery();
    const searchText = query.get('query') || '';
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    
    // Search input state
    const [searchInput, setSearchInput] = useState(searchText);

    // Filter and pagination states
    const [filterVisible, setFilterVisible] = useState(false);
    const filterRef = useRef(null);

    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(1000);
    const [maxAvailablePrice, setMaxAvailablePrice] = useState(1000);
    const [brands, setBrands] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!searchText) {
            setProducts([]);
            return;
        }
        const fetchProducts = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get('/items', {
                    params: { search: searchText }
                });
                // Ensure response.data is an array
                let fetchedProducts = [];
                if (Array.isArray(response.data)) {
                    fetchedProducts = response.data;
                } else if (response.data && Array.isArray(response.data.items)) {
                    fetchedProducts = response.data.items;
                } else {
                    fetchedProducts = [];
                }

                // Extract unique brands
                const uniqueBrands = [...new Set(fetchedProducts.map(p => p.brand).filter(Boolean))];
                setBrands(uniqueBrands);

                // Determine max available price
                const prices = fetchedProducts.map(p => p.sell_price);
                const maxPriceInData = prices.length > 0 ? Math.max(...prices) : 1000;
                setMaxAvailablePrice(maxPriceInData);

                // Clamp minPrice and maxPrice
                setMinPrice(prev => (prev < 0 ? 0 : prev));
                setMaxPrice(prev => (prev > maxPriceInData ? maxPriceInData : prev));

                // Filter products based on price and brand
                let filteredProducts = fetchedProducts;
                if (minPrice !== '') {
                    filteredProducts = filteredProducts.filter(p => p.sell_price >= minPrice);
                }
                if (maxPrice !== '') {
                    filteredProducts = filteredProducts.filter(p => p.sell_price <= maxPrice);
                }
                if (selectedBrands.length > 0) {
                    filteredProducts = filteredProducts.filter(p => selectedBrands.includes(p.brand));
                }

                setProducts(filteredProducts);
                setCurrentPage(1);
            } catch (err) {
                setError('Failed to fetch products');
                setProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [searchText, minPrice, maxPrice, selectedBrands]);

    const handleAddToCart = (product) => {
        if (onAddToCart) {
            onAddToCart(product);
        }
    };

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const handleItemClick = async (product) => {
        setModalLoading(true);
        try {
            const response = await axios.get(`/albums/${product.item_id}`);
            const photos = response.data.length > 0
                ? response.data.map(album => album.photo_url)
                : [product.photo_url, product.photo_url, product.photo_url]; // fallback

            setSelectedProduct({ ...product, photos });
        } catch (error) {
            console.error('Error fetching album photos:', error);
            const photos = product.photos && product.photos.length > 0
                ? product.photos
                : [product.photo_url, product.photo_url, product.photo_url]; // fallback

            setSelectedProduct({ ...product, photos });
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedProduct(null);
    };

    // Pagination calculations
    const totalPages = Math.ceil(products.length / itemsPerPage);
    const pagedProducts = Array.isArray(products) ? products.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];

    const goToPage = (page) => {
        if (page < 1) page = 1;
        else if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchInput.trim()) {
            navigate(`/search?query=${encodeURIComponent(searchInput.trim())}`);
        }
    };

    if (!searchText) {
        return (
            <>
                <Header 
                    cartItems={cartItems}
                    cartList={cartList}
                    cartTotal={cartTotal}
                    showCartDropdown={showCartDropdown}
                    onCartToggle={onCartToggle}
                    onRemoveItem={onRemoveItem}
                />
                <div className={styles.searchContainer} style={{ paddingTop: '120px' }}>
                    <div className={styles.pageHeader}>
                        <h1 className={styles.searchTitle}>Search Products</h1>
                        <p className={styles.searchSubtitle}>
                            Find the perfect products for you
                        </p>
                        <form onSubmit={handleSearch} className={styles.searchForm}>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search products..."
                                className={styles.searchInput}
                            />
                        <button type="submit" className={styles.searchButton} onClick={handleSearch}>
                            Search
                        </button>
                        </form>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header 
                cartItems={cartItems}
                cartList={cartList}
                cartTotal={cartTotal}
                showCartDropdown={showCartDropdown}
                onCartToggle={onCartToggle}
                onRemoveItem={onRemoveItem}
            />

            <br/>            <br/>
            <br/>
            <br/>
            <div className={styles.searchContainer} style={{ paddingTop: '120px' }}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.searchTitle}>
                        Search Results for: <span className={styles.searchQuery}>"{searchText}"</span>
                    </h1>
                    <p className={styles.searchSubtitle}>
                        Discover products that match your search
                    </p>
                    <form onSubmit={handleSearch} className={styles.searchForm}>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search products..."
                            className={styles.searchInput}
                        />
                        <button type="submit" className={styles.searchButton}>
                            Search
                        </button>
                    </form>
                </div>

                <button
                    onClick={() => setFilterVisible(!filterVisible)}
                    className={styles.filterToggle}
                >
                    {filterVisible ? 'Hide Filters' : 'Show Filters'}
                </button>

                <div className={styles.contentWrapper}>
                    <div
                        ref={filterRef}
                        className={`${styles.filterSidebar} ${!filterVisible ? styles.hidden : ''}`}
                    >
                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Price</h3>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="minPrice" className={styles.priceLabel}>
                                    Min Price: <span className={styles.priceValue}>${minPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="minPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={minPrice}
                                    onChange={(e) => {
                                        const val = Math.min(Number(e.target.value), maxPrice);
                                        setMinPrice(val);
                                        setCurrentPage(1);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="maxPrice" className={styles.priceLabel}>
                                    Max Price: <span className={styles.priceValue}>${maxPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="maxPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={maxPrice}
                                    onChange={(e) => {
                                        const val = Math.max(Number(e.target.value), minPrice);
                                        setMaxPrice(val);
                                        setCurrentPage(1);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Brand</h3>
                            <div className={styles.brandList}>
                                {brands.length === 0 && (
                                    <div className={styles.noProducts}>No brands available</div>
                                )}
                                {brands.map((brand) => (
                                    <div key={brand} className={styles.brandItem}>
                                        <input
                                            type="checkbox"
                                            id={`brand-${brand}`}
                                            checked={selectedBrands.includes(brand)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedBrands([...selectedBrands, brand]);
                                                } else {
                                                    setSelectedBrands(selectedBrands.filter(b => b !== brand));
                                                }
                                                setCurrentPage(1);
                                            }}
                                            className={styles.brandCheckbox}
                                        />
                                        <label htmlFor={`brand-${brand}`} className={styles.brandLabel}>
                                            {brand}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.productsContainer}>
                        <div className={styles.resultsInfo}>
                            <p className={styles.resultsCount}>
                                Found <span className={styles.resultsHighlight}>{products.length}</span> products matching your search
                            </p>
                        </div>

                        {loading ? (
                            <div className={styles.loadingContainer}>
                                <div className={styles.spinner}></div>
                            </div>
                        ) : error ? (
                            <div className={styles.errorMessage}>{error}</div>
                        ) : pagedProducts.length === 0 ? (
                            <div className={styles.noProducts}>
                                <div className={styles.noProductsIcon}>🔍</div>
                                <p>No products found matching your search criteria.</p>
                            </div>
                        ) : (
                            <Products
                                products={pagedProducts}
                                onAddToCart={handleAddToCart}
                                onItemClick={handleItemClick}
                                cartList={cartList}
                            />
                        )}

                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <button 
                                    onClick={() => goToPage(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                    className={styles.paginationButton}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => goToPage(i + 1)}
                                        className={`${styles.paginationButton} ${currentPage === i + 1 ? styles.activePage : ''}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button 
                                    onClick={() => goToPage(currentPage + 1)} 
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationButton}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ProductModal 
                product={selectedProduct}
                onClose={closeModal}
                onAddToCart={handleAddToCart}
                loading={modalLoading}
            />

            <br/>            <br/>
            <br/>
            <br/>
            <br/><br/>
            <br/>
        </>
    );
};

export default SearchProducts;
